import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase, SUPABASE_PROJECT_REF } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useEffect } from 'react';
import { logger } from '@/lib/logger';
import { 
  calculateTrend, 
  calculateInvertedTrend, 
  getPeriodDateRanges, 
  toISODateString 
} from '@/lib/trendCalculations';

export interface ChefStats {
  dailyOrders: number;
  validatedOrders: number;
  deliveriesInProgress: number;
  nextDeliveryTime: string | null;
  volumePoured: number;
  successfulDeliveries: number;
  incidents: number;
  totalIncidents: number;
  volumeTrend: number | null;
  deliveriesTrend: number | null;
  incidentsTrend: number | null;
  trucksCount: number;
  /** Previous period values for "Semaine dernière: X m³" display (0 when period is 'all') */
  previousVolume?: number;
  previousDeliveries?: number;
}

export interface DailyOrder {
  id: string;
  date_commande: string;
  date_livraison: string;
  desiredTime: string;
  volume_commande: number;
  statut: string;
  priorite: string;
  commentaire: string | null;
  chantier_name: string;
  formule_name: string;
  formule_resistance: string | null;
  site?: {
    id: string;
    name: string;
  };
  fournisseur?: {
    id: string;
    name: string;
    centrale_phone?: string | null;
  };
  ouvrages: Array<{
    id: string;
    workItemType: string;
    building: string;
    level: string;
    volume: number;
    name: string;
    actual_volume_poured: number | null;
  }>;
  camions: Array<{
    id: string;
    truck_number: number;
    ordered_volume: number;
    scheduled_time: string;
    status: string;
    crane_access?: string | null;
    options?: Array<{
      id: string;
      name: string;
      category: string;
      price: number;
    }>;
    formula?: {
      name: string;
      strength: string | null;
      exposure_class?: string | null;
      consistency?: string | null;
      granulometry?: string | null;
    };
    deliveries?: Array<{
      delivery_note_code?: string;
      arrival_time?: string | null;
      delivery_incidents?: Array<{
        id: string;
        incident_type: string;
      }>;
    }>;
  }>;
  livraisons: Array<{
    id: string;
    statut: string;
    volume_livre: number;
    heure_arrivee: string | null;
  }>;
}

export interface NextDelivery {
  id: string;
  numero_camion: number;
  volume_camion: number;
  heure_prevue: string;
  formule_name: string;
  eta_minutes: number;
}

interface ChefStatsData {
  stats: ChefStats | null;
  dailyOrders: DailyOrder[];
  nextDeliveries: NextDelivery[];
}

// Exported for prefetching in SiteManagerLayout
export async function fetchSiteManagerStats(
  userId: string | undefined,
  selectedChantierId: string | null | undefined,
  period: 'week' | 'biweek' | 'month' | 'all' = 'month'
): Promise<ChefStatsData> {
  if (!userId) {
    return { stats: null, dailyOrders: [], nextDeliveries: [] };
  }

  const { data, error } = await supabase.functions.invoke('get-site-dashboard', {
    body: { siteId: selectedChantierId, period },
  });

  if (error) {
    logger.error('ChefStats', 'Edge function error, falling back to direct queries', error);
    return fetchSiteManagerStatsDirectly(userId, selectedChantierId, period);
  }

  return data as ChefStatsData;
}

// Fallback function with direct Supabase queries
async function fetchSiteManagerStatsDirectly(
  userId: string,
  selectedChantierId: string | null | undefined,
  period: 'week' | 'biweek' | 'month' | 'all' = 'month'
): Promise<ChefStatsData> {
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const sevenDaysFromNow = new Date(today);
  sevenDaysFromNow.setDate(today.getDate() + 7);
  const sevenDaysStr = sevenDaysFromNow.toISOString().split('T')[0];

  // Use shared utility for period date calculations ('all' = depuis le début, pas de période précédente)
  const periodStartStr = period === 'all'
    ? '2000-01-01'
    : toISODateString(getPeriodDateRanges(period, today).current.start);
  const ranges = period === 'all' ? null : getPeriodDateRanges(period, today);
  const previousPeriodStartStr = ranges ? toISODateString(ranges.previous.start) : null;
  const previousPeriodEndStr = ranges ? toISODateString(ranges.previous.end) : null;

  // 1. Déterminer les chantiers accessibles à l'utilisateur
  let siteIds: string[] = [];
  
  if (selectedChantierId) {
    siteIds = [selectedChantierId];
  } else {
    // Use centralized site access logic from useAuthorizedSiteIds
    const { fetchAuthorizedSiteIds } = await import('@/hooks/queries/useAuthorizedSiteIds');
    const { siteIds: authorizedIds } = await fetchAuthorizedSiteIds(userId, undefined);
    siteIds = authorizedIds;
  }

  if (siteIds.length === 0) {
    return { 
      stats: {
        dailyOrders: 0,
        validatedOrders: 0,
        deliveriesInProgress: 0,
        nextDeliveryTime: null,
        volumePoured: 0,
        successfulDeliveries: 0,
        incidents: 0,
        totalIncidents: 0,
        volumeTrend: null,
        deliveriesTrend: null,
        incidentsTrend: null,
        trucksCount: 0,
        previousVolume: 0,
        previousDeliveries: 0,
      }, 
      dailyOrders: [], 
      nextDeliveries: [] 
    };
  }

  // 2. REQUÊTE PARALLÈLE : Données historiques (KPIs) + Période précédente (si pas 'all') + Données à venir
  const [historicalResult, previousPeriodResult, upcomingResult] = await Promise.all([
    // Requête historique pour les KPIs (période passée jusqu'à aujourd'hui)
    supabase
      .from('concrete_pours')
      .select(`
        id,
        status,
        concrete_deliveries (
          id, status, delivered_volume
        )
      `)
      .in('site_id', siteIds)
      .gte('delivery_date', periodStartStr)
      .lte('delivery_date', todayStr)
      .is('deleted_at', null),
    
    // Requête pour la période précédente (pour calcul des tendances) — skip si 'all'
    previousPeriodStartStr && previousPeriodEndStr
      ? supabase
          .from('concrete_pours')
          .select(`
            id,
            status,
            concrete_deliveries (
              id, status, delivered_volume
            )
          `)
          .in('site_id', siteIds)
          .gte('delivery_date', previousPeriodStartStr)
          .lte('delivery_date', previousPeriodEndStr)
          .is('deleted_at', null)
      : Promise.resolve({ data: [] as any[] }),
    
    // Requête pour les prochains bétonnages (7 jours)
    supabase
      .from('concrete_pours')
      .select(`
        *,
        sites!fk_commandes_journalieres_chantier (id, name),
        formula:order_formulas!betonnages_formule_beton_id_fkey (id, name, strength, exposure_class, consistency, granulometry),
        suppliers (id, name, plant_phone),
        concrete_plants:plant_id (email, city, phone),
        pour_work_items (
          id, volume_poured, actual_volume_poured, desired_time,
          work_items (id, name, building, level)
        ),
        concrete_deliveries (
          id, truck_number, ordered_volume, scheduled_time, status,
          delivered_volume, delivery_note_code, arrival_time, crane_access,
          concrete_delivery_options (id, name, category, price, active)
        )
      `)
      .in('site_id', siteIds)
      .gte('delivery_date', todayStr)
      .lte('delivery_date', sevenDaysStr)
      .is('deleted_at', null)
      .order('delivery_date', { ascending: true })
      .order('desired_time', { ascending: true })
  ]);

  if (historicalResult.error) throw historicalResult.error;
  if (previousPeriodResult.error) throw previousPeriodResult.error;
  if (upcomingResult.error) throw upcomingResult.error;

  const historicalPours = historicalResult.data || [];
  const previousPeriodPours = previousPeriodResult.data || [];
  const upcomingPours = upcomingResult.data || [];

  // 3. Calculer les KPIs historiques (Volume coulé, Livraisons OK)
  let volumePoured = 0;
  let successfulDeliveries = 0;

  historicalPours.forEach(pour => {
    (pour.concrete_deliveries || []).forEach((d: any) => {
      if (['completed', 'delivered_with_incident'].includes(d.status)) {
        volumePoured += Number(d.delivered_volume || 0);
        successfulDeliveries++;
      }
    });
  });

  // 3b. Calculer les KPIs de la période précédente
  let previousVolume = 0;
  let previousDeliveries = 0;

  previousPeriodPours.forEach(pour => {
    (pour.concrete_deliveries || []).forEach((d: any) => {
      if (['completed', 'delivered_with_incident'].includes(d.status)) {
        previousVolume += Number(d.delivered_volume || 0);
        previousDeliveries++;
      }
    });
  });

  // 4. Calculer les stats des bétonnages à venir (En cours, Incidents)
  const deliveriesInProgress = upcomingPours.filter(o => 
    ['in_progress', 'order_sent', 'awaiting_confirmation', 'edit_pending', 'edit_confirmed'].includes(o.status)
  ).length;

  // Récupérer les incidents - PARALLEL pour upcoming ET previous period
  const allDeliveryIds = upcomingPours.flatMap(p => 
    (p.concrete_deliveries || []).map((d: any) => d.id)
  );

  const previousDeliveryIds = previousPeriodPours.flatMap(p => 
    (p.concrete_deliveries || []).map((d: any) => d.id)
  );

  // Parallel fetch for both incident sets (~500ms saved)
  const [upcomingIncidentsResult, previousIncidentsResult] = await Promise.all([
    allDeliveryIds.length > 0
      ? supabase
          .from('delivery_incidents')
          .select('id, truck_id, incident_type, resolved')
          .in('truck_id', allDeliveryIds)
      : Promise.resolve({ data: [] as any[] }),
    previousDeliveryIds.length > 0
      ? supabase
          .from('delivery_incidents')
          .select('id, resolved')
          .in('truck_id', previousDeliveryIds)
      : Promise.resolve({ data: [] as any[] }),
  ]);

  const incidentsMap = new Map<string, any[]>();
  let totalUnresolvedIncidents = 0;

  (upcomingIncidentsResult.data || []).forEach((inc: any) => {
    if (!incidentsMap.has(inc.truck_id)) {
      incidentsMap.set(inc.truck_id, []);
    }
    incidentsMap.get(inc.truck_id)!.push(inc);
    if (!inc.resolved) totalUnresolvedIncidents++;
  });

  const previousIncidents = (previousIncidentsResult.data || []).filter((inc: any) => !inc.resolved).length;

  // Trouver la prochaine livraison
  let nextDeliveryTime: string | null = null;
  for (const pour of upcomingPours) {
    const deliveries = pour.concrete_deliveries || [];
    for (const d of deliveries as any[]) {
      if (d.status === 'scheduled' && !nextDeliveryTime) {
        nextDeliveryTime = d.scheduled_time;
        break;
      }
    }
    if (nextDeliveryTime) break;
  }

  // Calculer le nombre total de camions en cours
  const trucksCount = upcomingPours.reduce((count, pour) => {
    const activeDeliveries = (pour.concrete_deliveries || []).filter((d: any) => 
      ['scheduled', 'in_transit', 'on_site'].includes(d.status)
    );
    return count + activeDeliveries.length;
  }, 0);

  // Calculer les tendances using shared utility
  const volumeTrend = calculateTrend(volumePoured, previousVolume);
  const deliveriesTrend = calculateTrend(successfulDeliveries, previousDeliveries);
  // Pour les incidents, utiliser la même logique (moins = pire trend)
  const incidentsTrend = calculateInvertedTrend(totalUnresolvedIncidents, previousIncidents);

  // Calcul du total d'incidents sur le chantier depuis les données déjà récupérées (évite une requête supplémentaire)
  let totalIncidentsOnSite = 0;
  incidentsMap.forEach((incidents) => {
    totalIncidentsOnSite += incidents.length;
  });

  const stats: ChefStats = {
    dailyOrders: upcomingPours.length,
    validatedOrders: upcomingPours.filter(o => o.status === 'completed').length,
    deliveriesInProgress,
    nextDeliveryTime,
    volumePoured,
    successfulDeliveries,
    incidents: totalUnresolvedIncidents,
    totalIncidents: totalIncidentsOnSite,
    volumeTrend,
    deliveriesTrend,
    incidentsTrend,
    trucksCount,
    previousVolume,
    previousDeliveries,
  };

  // 5. Enrichir les orders à venir avec les incidents
  const enrichedOrders: DailyOrder[] = upcomingPours.map((order: any) => {
    const site = order.sites;
    const formula = order.formula;
    const supplier = order.suppliers;
    const plant = order.concrete_plants;
    const workItems = order.pour_work_items || [];
    const deliveries = order.concrete_deliveries || [];

    return {
      id: order.id,
      date_commande: order.order_date,
      date_livraison: order.delivery_date,
      desiredTime: order.desired_time,
      volume_commande: order.ordered_volume,
      statut: order.status,
      priorite: order.priority,
      commentaire: order.comment,
      chantier_name: site?.name || 'Inconnu',
      formule_name: formula?.name || 'Inconnue',
      formule_resistance: formula?.strength || null,
      site: site ? { id: site.id, name: site.name } : undefined,
      fournisseur: supplier ? {
        id: supplier.id,
        name: supplier.name,
        centrale_phone: plant?.phone || supplier.plant_phone,
      } : undefined,
      ouvrages: workItems.map((wi: any) => ({
        id: wi.work_items?.id || '',
        workItemType: wi.work_items?.name || '',
        building: wi.work_items?.building || '',
        level: wi.work_items?.level || '',
        volume: wi.volume_poured || 0,
        name: wi.work_items?.name || '',
        actual_volume_poured: wi.actual_volume_poured,
      })),
      camions: deliveries
        .sort((a: any, b: any) => {
          const timeCompare = (a.scheduled_time || '').localeCompare(b.scheduled_time || '');
          if (timeCompare !== 0) return timeCompare;
          return (a.truck_number || 0) - (b.truck_number || 0);
        })
        .map((c: any) => ({
        id: c.id,
        truck_number: c.truck_number,
        ordered_volume: c.ordered_volume,
        scheduled_time: c.scheduled_time,
        status: c.status,
        crane_access: c.crane_access || null,
        options: (c.concrete_delivery_options || []).filter((o: any) => o.active),
        formula: formula ? { name: formula.name, strength: formula.strength, exposure_class: formula.exposure_class, consistency: formula.consistency, granulometry: formula.granulometry } : undefined,
        deliveries: [{
          delivery_note_code: c.delivery_note_code,
          arrival_time: c.arrival_time,
          delivery_incidents: incidentsMap.get(c.id) || [],
        }],
      })),
      livraisons: [],
    };
  });

  return {
    stats,
    dailyOrders: enrichedOrders,
    nextDeliveries: [],
  };
}

/**
 * React Query hook for fetching chef de chantier statistics and orders
 * @param selectedChantierId - Optional site ID to filter by
 * @param period - Period for historical KPIs: 'week', 'biweek' (quinzaine), or 'month'
 */
export function useSiteManagerStatsQuery(
  selectedChantierId?: string | null,
  period: 'week' | 'biweek' | 'month' | 'all' = 'month'
) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['chefStats', SUPABASE_PROJECT_REF, user?.id, selectedChantierId, period],
    queryFn: () => fetchSiteManagerStats(user?.id, selectedChantierId, period),
    enabled: !!user?.id,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,  // Auto-refresh every 5 minutes
    refetchIntervalInBackground: false, // Don't refresh when tab is hidden
    placeholderData: (previousData) => previousData, // Keep old data visible during refetch
  });
}

/**
 * Hook for realtime updates to chef stats
 * Listens to concrete_pours and delivery_incidents changes
 */
export function useSiteManagerStatsRealtime(siteId: string | undefined) {
  const queryClient = useQueryClient();
  
  useEffect(() => {
    if (!siteId) return;
    
    const channel = supabase
      .channel(`chef-stats-${siteId}`)
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'concrete_pours',
          filter: `site_id=eq.${siteId}`
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['chefStats'] });
        }
      )
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'concrete_deliveries'
        },
        (payload) => {
          // Optimisation: vérifier si la livraison appartient au site actuel via le cache
          const pourId = (payload.new as any)?.concrete_pour_id || (payload.old as any)?.concrete_pour_id;
          if (!pourId) {
            // Fallback: invalider si on ne peut pas déterminer
            queryClient.invalidateQueries({ queryKey: ['chefStats'] });
            return;
          }
          
          // Vérifier dans le cache React Query si ce pour appartient au site
          const cachedData = queryClient.getQueryData(['chefStats']) as { dailyOrders?: Array<{ id: string }> } | undefined;
          const belongsToCurrentSite = cachedData?.dailyOrders?.some((order) => order.id === pourId);
          
          if (belongsToCurrentSite) {
            queryClient.invalidateQueries({ queryKey: ['chefStats'] });
          }
          // Si le pour n'est pas dans le cache du site actuel, on ignore l'événement
        }
      )
      .on(
        'postgres_changes',
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'delivery_incidents'
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['chefStats'] });
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [siteId, queryClient]);
}
